import { usePhoneFormatter } from '@/hooks/use-phone-formatter'
import type { CountryCode } from 'libphonenumber-js/min'
import { useIsReadOnly } from '@/lib/casl'
import { Country } from '@/lib/enums/Country'
import { truncateFieldValueToCharacterLimit } from '@/lib/field-character-limits'
import { useFieldContext, useFormContext } from '@/lib/form-contexts'
import { cn } from '@/lib/utils'
import { type ChangeEvent, type KeyboardEvent, useId } from 'react'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Typography } from '@/components/ui/typography'
import TextInput, { type TextInputProps } from './TextInput'

function getDigitCountBeforeCaret(value: string, caretPosition: number) {
  return String(value).slice(0, caretPosition).replace(/\D/g, '').length
}

function getCaretPositionAfterDigitCount(value: string, digitCount: number) {
  if (digitCount <= 0) {
    return 0
  }

  let seenDigits = 0

  for (let index = 0; index < value.length; index += 1) {
    if (/\d/.test(value.charAt(index))) {
      seenDigits += 1

      if (seenDigits >= digitCount) {
        return index + 1
      }
    }
  }

  return value.length
}

function restoreCaretAfterFormat(
  input: HTMLInputElement,
  displayValue: string,
  digitCountBeforeCaret: number
) {
  const restore = () => {
    if (document.activeElement !== input) {
      return
    }

    const nextCaretPosition = getCaretPositionAfterDigitCount(displayValue, digitCountBeforeCaret)
    input.setSelectionRange(nextCaretPosition, nextCaretPosition)
  }

  if (typeof window.queueMicrotask === 'function') {
    window.queueMicrotask(restore)
    return
  }

  setTimeout(restore, 0)
}

export type PhoneInputProps = TextInputProps & {
  countryCode?: CountryCode
}

export default function PhoneInput({
  id,
  label = null,
  countryCode = Country.UnitedStates.Value,
  labelEnd = '',
  placeholder,
  className = '',
  characterMaxLength = null,
  ...props
}: PhoneInputProps) {
  const field = useFieldContext<string | null | undefined>()

  const form = useFormContext()
  const randomId = useId()
  const inputId = id || `${randomId}-${field.name}`
  const { formatPhone, isValidPhone, formatToE164 } = usePhoneFormatter()
  const displayValue = field.state.value ? formatPhone(field.state.value, countryCode) : ''
  const { isReadOnly } = useIsReadOnly()

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target
    const nextInputValue = input.value
    const digitCountBeforeCaret = getDigitCountBeforeCaret(
      nextInputValue,
      input.selectionStart ?? nextInputValue.length
    )
    const isValid = isValidPhone(nextInputValue, countryCode)
    const nextFieldValue = truncateFieldValueToCharacterLimit(
      isValid ? formatToE164(nextInputValue, countryCode) : nextInputValue,
      characterMaxLength
    )
    const nextDisplayValue = nextFieldValue ? formatPhone(nextFieldValue, countryCode) : ''

    field.handleChange(nextFieldValue)
    restoreCaretAfterFormat(input, nextDisplayValue, digitCountBeforeCaret)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      const input = e.currentTarget
      const cursorPosition = input.selectionStart

      // If cursor is at a formatting character, move it back one position
      if (cursorPosition !== null && cursorPosition > 0) {
        const charAtCursor = displayValue[cursorPosition - 1]
        if (
          charAtCursor === '(' ||
          charAtCursor === ')' ||
          charAtCursor === ' ' ||
          charAtCursor === '-'
        ) {
          e.preventDefault()

          // Find the previous non-formatting character
          let newPosition = cursorPosition - 1
          while (newPosition > 0) {
            const char = displayValue[newPosition - 1]
            if (char !== '(' && char !== ')' && char !== ' ' && char !== '-') {
              break
            }
            newPosition--
          }

          input.setSelectionRange(newPosition, newPosition)
        }
      }
    }
  }

  return (
    <div className={cn('flex flex-col gap-y-2 w-full', className)}>
      {!!label && (
        <div className="inline-flex items-center">
          <Label htmlFor={inputId}>{label}</Label>
          {labelEnd}
        </div>
      )}
      {/* // This won't change for a hot minute. When it does, let's use this: https://shadcn-phone-input.vercel.app/ */}
      {isReadOnly ? (
        <Typography variant="label" tone={!displayValue ? 'muted' : 'default'}>
          {displayValue}
        </Typography>
      ) : (
        <>
          <Input
            id={`${randomId}_phone_country_code`}
            type="hidden"
            value={
              countryCode || form.state.values.phone_country_code || Country.UnitedStates.Value
            }
          />
          <TextInput
            id={inputId}
            type={'tel'}
            placeholder={placeholder ?? '(555) 555-5555'}
            value={displayValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            {...props}
          />
        </>
      )}
    </div>
  )
}
